import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Td,
  Text,
  Textarea,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FaPlaneDeparture } from "react-icons/fa";
import { EditIcon, CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import {
  cancelConsultantLeaveRequest,
  createConsultantLeaveRequest,
  listConsultantLeaveRequests,
  submitConsultantLeaveRequest,
  updateConsultantLeaveRequest,
} from "../../../api/consultantPortalApi";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
} from "../../../components/Portal/ui";

// Status -> Chakra colour scheme for StatusBadge dots (registered scales only).
const STATUS_SCHEME = {
  draft: "gray",
  submitted: "blue",
  approved: "green",
  rejected: "red",
  cancelled: "orange",
};

const LEAVE_TYPE_KEYS = ["annual_leave", "sick_leave", "unpaid_leave", "rtt", "other"];

const FILTER_SELECT_SX = {
  fontWeight: 600,
};

const createInitialForm = () => ({
  leaveType: "annual_leave",
  startDate: "",
  endDate: "",
  notes: "",
});

const LeaveRequests = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createInitialForm);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listConsultantLeaveRequests();
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast({
        title: t("consultantPortal.leaveRequests.toasts.loadFailedTitle"),
        description: error?.response?.data?.detail || t("consultantPortal.leaveRequests.toasts.loadFailedDescription"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setForm(createInitialForm());
  }, []);

  const handleEdit = useCallback((item) => {
    setEditingId(item.id);
    setForm({
      leaveType: item.leaveType,
      startDate: item.startDate,
      endDate: item.endDate,
      notes: item.notes || "",
    });
  }, []);

  const handleSave = useCallback(
    async ({ submitAfterSave = false }) => {
      if (form.startDate && form.endDate && form.endDate < form.startDate) {
        toast({
          title: t("consultantPortal.leaveRequests.validation.invalidDatesTitle"),
          description: t("consultantPortal.leaveRequests.validation.endBeforeStart"),
          status: "error",
        });
        return;
      }
      setSaving(true);
      try {
        let saved;
        if (editingId) {
          saved = await updateConsultantLeaveRequest(editingId, form);
        } else {
          saved = await createConsultantLeaveRequest(form);
        }

        if (submitAfterSave) {
          await submitConsultantLeaveRequest(saved.id);
        }

        toast({
          title: submitAfterSave ? t("consultantPortal.leaveRequests.toasts.submitted") : t("consultantPortal.leaveRequests.toasts.saved"),
          status: "success",
        });
        resetForm();
        fetchRequests();
      } catch (error) {
        toast({
          title: t("consultantPortal.leaveRequests.toasts.saveFailedTitle"),
          description: error?.response?.data?.detail || t("consultantPortal.leaveRequests.toasts.saveFailedDescription"),
          status: "error",
        });
      } finally {
        setSaving(false);
      }
    },
    [editingId, fetchRequests, form, resetForm, toast, t]
  );

  const handleCancel = useCallback(
    async (item) => {
      try {
        await cancelConsultantLeaveRequest(item.id);
        toast({ title: t("consultantPortal.leaveRequests.toasts.cancelled"), status: "success" });
        fetchRequests();
      } catch (error) {
        toast({
          title: t("consultantPortal.leaveRequests.toasts.cancelFailedTitle"),
          description: error?.response?.data?.detail || t("consultantPortal.leaveRequests.toasts.cancelFailedDescription"),
          status: "error",
        });
      }
    },
    [fetchRequests, toast, t]
  );

  const sortedItems = useMemo(() => {
    return [...items].sort((left, right) => left.startDate.localeCompare(right.startDate));
  }, [items]);

  const labelStyle = {
    fontSize: "11px",
    fontWeight: "800",
    color: "portal.muted",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: "4px",
  };

  const endInvalid = form.startDate && form.endDate && form.endDate < form.startDate;

  const columns = [
    { key: "type", label: t("consultantPortal.leaveRequests.table.type"), width: "18%" },
    { key: "dates", label: t("consultantPortal.leaveRequests.table.dates"), width: "24%" },
    { key: "status", label: t("consultantPortal.leaveRequests.table.status"), width: "18%" },
    { key: "review", label: t("consultantPortal.leaveRequests.table.review") },
    { key: "actions", label: "", width: "14%", align: "right" },
  ];

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CP"
        eyebrow={t("consultantPortal.common.eyebrow", "Consultant Portal")}
        title={t("consultantPortal.leaveRequests.pageTitle")}
        actions={
          editingId ? (
            <Button variant="secondary" size="sm" onClick={resetForm}>
              {t("consultantPortal.leaveRequests.newRequest")}
            </Button>
          ) : null
        }
      />

      <Box p="20px">
        <Flex gap="12px" align="stretch" direction={{ base: "column", xl: "row" }}>
          <Box flex="1" minW={0} display="flex">
            <Panel
              w="100%"
              title={
                editingId
                  ? t("consultantPortal.leaveRequests.formTitleEdit")
                  : t("consultantPortal.leaveRequests.formTitleNew")
              }
            >
              <Box p="14px">
                <FormControl mb="12px">
                  <FormLabel sx={labelStyle}>
                    {t("consultantPortal.leaveRequests.fields.leaveType")}
                  </FormLabel>
                  <Select
                    value={form.leaveType}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, leaveType: event.target.value }))
                    }
                    sx={FILTER_SELECT_SX}
                  >
                    {LEAVE_TYPE_KEYS.map((value) => (
                      <option key={value} value={value}>
                        {t(`consultantPortal.leaveRequests.leaveTypes.${value}`)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Flex gap="12px" direction={{ base: "column", md: "row" }}>
                  <FormControl>
                    <FormLabel sx={labelStyle}>
                      {t("consultantPortal.leaveRequests.fields.startDate")}
                    </FormLabel>
                    <Input
                      type="date"
                      fontWeight="600"
                      value={form.startDate}
                      onChange={(event) => {
                        const nextStart = event.target.value;
                        setForm((current) => {
                          const next = { ...current, startDate: nextStart };
                          const endEmpty = !current.endDate;
                          const endBeforeStart =
                            current.endDate && nextStart && current.endDate < nextStart;
                          if (endEmpty || endBeforeStart) {
                            next.endDate = nextStart;
                          }
                          return next;
                        });
                      }}
                    />
                  </FormControl>
                  <FormControl isInvalid={Boolean(endInvalid)}>
                    <FormLabel sx={labelStyle}>
                      {t("consultantPortal.leaveRequests.fields.endDate")}
                    </FormLabel>
                    <Input
                      type="date"
                      fontWeight="600"
                      min={form.startDate || undefined}
                      value={form.endDate}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, endDate: event.target.value }))
                      }
                    />
                    {endInvalid && (
                      <Text fontSize="12px" color="portal.danger" mt="4px" sx={{ marginBottom: "0 !important" }}>
                        {t("consultantPortal.leaveRequests.validation.endBeforeStart")}
                      </Text>
                    )}
                  </FormControl>
                </Flex>
                <FormControl mt="12px">
                  <FormLabel sx={labelStyle}>
                    {t("consultantPortal.leaveRequests.fields.notes")}
                  </FormLabel>
                  <Textarea
                    rows={5}
                    fontWeight="600"
                    value={form.notes}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, notes: event.target.value }))
                    }
                    placeholder={t("consultantPortal.leaveRequests.placeholders.notes")}
                  />
                </FormControl>
                <HStack mt="16px" spacing="8px" flexWrap="wrap">
                  <Button variant="secondary" size="sm" isLoading={saving} onClick={() => handleSave({ submitAfterSave: false })}>
                    {t("consultantPortal.leaveRequests.buttons.saveDraft")}
                  </Button>
                  <Button variant="primary" size="sm" isLoading={saving} onClick={() => handleSave({ submitAfterSave: true })}>
                    {t("consultantPortal.leaveRequests.buttons.submitRequest")}
                  </Button>
                </HStack>
              </Box>
            </Panel>
          </Box>

          <Box flex="1.2" minW={0} display="flex">
            <Panel
              w="100%"
              title={t("consultantPortal.leaveRequests.listTitle")}
              count={loading ? t("consultantPortal.common.loading", "Loading...") : sortedItems.length}
            >
              <DataTable
                columns={columns}
                rows={sortedItems}
                loading={loading}
                emptyState={
                  <EmptyState
                    icon={<FaPlaneDeparture />}
                    title={t("consultantPortal.leaveRequests.empty")}
                    hint={t("consultantPortal.leaveRequests.emptyHint", "Submit a request to book time off.")}
                  />
                }
                renderRow={(item) => (
                  <Tr key={item.id}>
                    <Td fontWeight="700">
                      {LEAVE_TYPE_KEYS.includes(item.leaveType)
                        ? t(`consultantPortal.leaveRequests.leaveTypes.${item.leaveType}`)
                        : item.leaveType}
                    </Td>
                    <Td>{`${item.startDate} → ${item.endDate}`}</Td>
                    <Td>
                      <StatusBadge
                        label={t(`consultantPortal.leaveRequests.statusLabels.${item.status}`, item.status)}
                        colorScheme={STATUS_SCHEME[item.status] || "gray"}
                      />
                    </Td>
                    <Td color="portal.muted" title={item.reviewComment || ""}>
                      {item.reviewComment || "-"}
                    </Td>
                    <Td>
                      <HStack justify="flex-end" spacing="6px">
                        {(item.status === "draft" || item.status === "rejected") && (
                          <MiniIconButton
                            aria-label={t("consultantPortal.leaveRequests.buttons.edit")}
                            icon={<EditIcon />}
                            onClick={() => handleEdit(item)}
                          />
                        )}
                        {(item.status === "draft" || item.status === "submitted") && (
                          <MiniIconButton
                            aria-label={t("consultantPortal.leaveRequests.buttons.cancel")}
                            icon={<CloseIcon />}
                            color="portal.danger"
                            _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                            onClick={() => handleCancel(item)}
                          />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                )}
              />
            </Panel>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default LeaveRequests;
